import { useState, useEffect } from 'react';
import axios from 'axios';
import { BarChart3, FileText, TrendingUp, Calendar, Download, Building, Users, DollarSign, Wrench, CreditCard, PiggyBank, Scale, CheckCircle2, AlertCircle } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, LineChart, Line } from 'recharts';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const COLORS = ['#4f46e5', '#7c3aed', '#06b6d4', '#10b981', '#f59e0b', '#ef4444'];

export default function Reports() {
  const [properties, setProperties] = useState([]);
  const [selectedProperty, setSelectedProperty] = useState('');
  const [activeReport, setActiveReport] = useState('financial');
  const [financialData, setFinancialData] = useState(null);
  const [agedAR, setAgedAR] = useState(null);
  const [agedAP, setAgedAP] = useState(null);
  const [boardReport, setBoardReport] = useState(null);
  const [reconciliationReport, setReconciliationReport] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchProperties();
  }, []);

  useEffect(() => {
    if (selectedProperty || activeReport !== 'board') {
      fetchReportData();
    }
  }, [selectedProperty, activeReport]);

  const fetchProperties = async () => {
    try {
      const response = await axios.get(`${API}/corporations`);
      setProperties(response.data);
      if (response.data.length > 0) {
        setSelectedProperty(response.data[0].id);
      }
    } catch (error) {
      console.error('Failed to fetch properties:', error);
    }
  };

  const fetchReportData = async () => {
    setLoading(true);
    try {
      if (activeReport === 'financial') {
        const response = await axios.get(`${API}/reports/financial-summary${selectedProperty ? `?entity_id=${selectedProperty}` : ''}`);
        setFinancialData(response.data);
      } else if (activeReport === 'aged-ar') {
        const response = await axios.get(`${API}/reports/aged-ar${selectedProperty ? `?entity_id=${selectedProperty}` : ''}`);
        setAgedAR(response.data);
      } else if (activeReport === 'aged-ap') {
        const response = await axios.get(`${API}/reports/aged-ap${selectedProperty ? `?entity_id=${selectedProperty}` : ''}`);
        setAgedAP(response.data);
      } else if (activeReport === 'board' && selectedProperty) {
        const response = await axios.get(`${API}/reports/board-report?entity_id=${selectedProperty}`);
        setBoardReport(response.data);
      } else if (activeReport === 'reconciliation') {
        const response = await axios.get(`${API}/reports/reconciliation${selectedProperty ? `?entity_id=${selectedProperty}` : ''}`);
        setReconciliationReport(response.data);
      }
    } catch (error) {
      console.error('Failed to fetch report data:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-CA', { style: 'currency', currency: 'CAD', minimumFractionDigits: 0 }).format(amount || 0);
  };

  const downloadReport = () => {
    let csv = '';
    const now = new Date().toLocaleDateString();
    const propName = properties.find(p => p.id === selectedProperty)?.name || 'All Properties';

    if (activeReport === 'financial' && financialData) {
      csv = `Financial Summary Report\nProperty: ${propName}\nGenerated: ${now}\n\n`;
      csv += `Total Income,${financialData.total_income}\nTotal Expenses,${financialData.total_expenses}\nNet Income,${financialData.net_income}\n\n`;
      csv += 'Income by Category\nCategory,Amount\n';
      financialData.income_by_category.forEach(i => { csv += `${i.category},${i.amount}\n`; });
      csv += '\nExpenses by Category\nCategory,Amount\n';
      financialData.expenses_by_category.forEach(e => { csv += `${e.category},${e.amount}\n`; });
    } else if (activeReport === 'aged-ar' && agedAR) {
      csv = `Aged Accounts Receivable Report\nProperty: ${propName}\nAs of: ${agedAR.as_of_date}\nTotal Outstanding: ${agedAR.total_outstanding}\n\n`;
      csv += 'Aging,Amount,Count\n';
      csv += `Current,${agedAR.aged_buckets.current.amount},${agedAR.aged_buckets.current.count}\n`;
      csv += `1-30 Days,${agedAR.aged_buckets['1_30_days'].amount},${agedAR.aged_buckets['1_30_days'].count}\n`;
      csv += `31-60 Days,${agedAR.aged_buckets['31_60_days'].amount},${agedAR.aged_buckets['31_60_days'].count}\n`;
      csv += `61-90 Days,${agedAR.aged_buckets['61_90_days'].amount},${agedAR.aged_buckets['61_90_days'].count}\n`;
      csv += `Over 90 Days,${agedAR.aged_buckets.over_90_days.amount},${agedAR.aged_buckets.over_90_days.count}\n`;
    } else if (activeReport === 'aged-ap' && agedAP) {
      csv = `Aged Accounts Payable Report\nProperty: ${propName}\nAs of: ${agedAP.as_of_date}\nTotal Outstanding: ${agedAP.total_outstanding}\n\n`;
      csv += 'Aging,Amount,Count\n';
      csv += `Current,${agedAP.aged_buckets.current.amount},${agedAP.aged_buckets.current.count}\n`;
      csv += `1-30 Days,${agedAP.aged_buckets['1_30_days'].amount},${agedAP.aged_buckets['1_30_days'].count}\n`;
      csv += `31-60 Days,${agedAP.aged_buckets['31_60_days'].amount},${agedAP.aged_buckets['31_60_days'].count}\n`;
      csv += `61-90 Days,${agedAP.aged_buckets['61_90_days'].amount},${agedAP.aged_buckets['61_90_days'].count}\n`;
      csv += `Over 90 Days,${agedAP.aged_buckets.over_90_days.amount},${agedAP.aged_buckets.over_90_days.count}\n`;
    } else if (activeReport === 'board' && boardReport) {
      csv = `Board Report\nProperty: ${boardReport.corporation.name}\nReport Date: ${boardReport.report_date}\n\n`;
      csv += `Units\nTotal,${boardReport.units_summary.total}\nOccupied,${boardReport.units_summary.occupied}\nVacant,${boardReport.units_summary.vacant}\nOccupancy Rate,${boardReport.units_summary.occupancy_rate}%\n\n`;
      csv += `Financial Summary\nYTD Income,${boardReport.financial_summary.ytd_income}\nYTD Expenses,${boardReport.financial_summary.ytd_expenses}\nYTD Net Income,${boardReport.financial_summary.ytd_net_income}\nCash on Hand,${boardReport.financial_summary.total_cash_on_hand}\nReserve Fund,${boardReport.financial_summary.reserve_fund_balance}\n`;
    }

    if (csv) {
      const blob = new Blob([csv], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `altam_${activeReport}_report_${new Date().toISOString().split('T')[0]}.csv`;
      a.click();
    }
  };

  const reports = [
    { id: 'financial', name: 'Financial Summary', icon: TrendingUp },
    { id: 'aged-ar', name: 'Aged AR', icon: FileText },
    { id: 'aged-ap', name: 'Aged AP', icon: FileText },
    { id: 'reconciliation', name: 'Reconciliation', icon: Scale },
    { id: 'board', name: 'Board Report', icon: Building }
  ];

  const renderAgedBuckets = (data, title) => {
    if (!data) return null;
    const buckets = data.aged_buckets;
    const chartData = [
      { name: 'Current', amount: buckets.current.amount, count: buckets.current.count },
      { name: '1-30', amount: buckets['1_30_days'].amount, count: buckets['1_30_days'].count },
      { name: '31-60', amount: buckets['31_60_days'].amount, count: buckets['31_60_days'].count },
      { name: '61-90', amount: buckets['61_90_days'].amount, count: buckets['61_90_days'].count },
      { name: '90+', amount: buckets.over_90_days.amount, count: buckets.over_90_days.count }
    ];

    return (
      <div className="space-y-6">
        <div className="bg-gradient-to-r from-indigo-600 to-violet-600 rounded-xl p-6 text-white">
          <h3 className="text-lg font-semibold">{title}</h3>
          <p className="text-indigo-100 text-sm">As of {data.as_of_date}</p>
          <p className="text-3xl font-bold mt-2">{formatCurrency(data.total_outstanding)}</p>
        </div>

        <div className="bg-white rounded-xl border border-slate-200 p-5">
          <h4 className="font-semibold text-slate-900 mb-4">Aging Summary</h4>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey="name" tick={{ fontSize: 12 }} />
              <YAxis tick={{ fontSize: 12 }} tickFormatter={(v) => `$${v/1000}k`} />
              <Tooltip formatter={(value) => formatCurrency(value)} />
              <Bar dataKey="amount" fill="#4f46e5" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          {chartData.map((bucket, idx) => (
            <div key={bucket.name} className="bg-white rounded-xl border border-slate-200 p-4">
              <p className="text-xs text-slate-500">{bucket.name}</p>
              <p className="text-lg font-bold text-slate-900">{formatCurrency(bucket.amount)}</p>
              <p className="text-xs text-slate-400">{bucket.count} items</p>
            </div>
          ))}
        </div>

        {/* Detailed Invoice List */}
        {buckets.over_90_days.invoices && buckets.over_90_days.invoices.length > 0 && (
          <div className="bg-white rounded-xl border border-slate-200 p-5">
            <h4 className="font-semibold text-slate-900 mb-4">Over 90 Days - Action Required</h4>
            <table className="w-full">
              <thead><tr className="border-b"><th className="py-2 text-left text-xs text-slate-500">Invoice</th><th className="py-2 text-left text-xs text-slate-500">Entity</th><th className="py-2 text-right text-xs text-slate-500">Amount</th><th className="py-2 text-right text-xs text-slate-500">Days</th></tr></thead>
              <tbody>
                {buckets.over_90_days.invoices.map((inv, i) => (
                  <tr key={i} className="border-b last:border-0"><td className="py-2 text-sm">{inv.invoice_number}</td><td className="py-2 text-sm text-slate-600">{inv.entity_name || inv.supplier_name}</td><td className="py-2 text-sm text-right font-medium text-rose-600">{formatCurrency(inv.amount)}</td><td className="py-2 text-sm text-right text-rose-600">{inv.days_overdue}</td></tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-6" data-testid="reports-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Financial Reports</h1>
          <p className="text-slate-500">Generate and view financial reports</p>
        </div>
        <button onClick={downloadReport} className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700" data-testid="export-report-btn">
          <Download className="w-4 h-4" /> Export Report
        </button>
      </div>

      <div className="flex flex-wrap gap-2">
        {reports.map((report) => (
          <button key={report.id} onClick={() => setActiveReport(report.id)}
            className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors ${activeReport === report.id ? 'bg-gradient-to-r from-indigo-600 to-violet-600 text-white' : 'bg-white border border-slate-200 text-slate-700 hover:bg-slate-50'}`}>
            <report.icon className="w-4 h-4" />{report.name}
          </button>
        ))}
      </div>

      <div className="flex items-center gap-4">
        <label className="text-sm font-medium text-slate-700">Property:</label>
        <select value={selectedProperty} onChange={(e) => setSelectedProperty(e.target.value)} className="px-4 py-2 border border-slate-300 rounded-lg">
          <option value="">All Properties</option>
          {properties.map(p => <option key={p.id} value={p.id}>{p.name} ({p.property_type})</option>)}
        </select>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div></div>
      ) : (
        <>
          {activeReport === 'financial' && financialData && (
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-5">
                  <p className="text-sm text-emerald-600">Total Income</p>
                  <p className="text-2xl font-bold text-emerald-700">{formatCurrency(financialData.total_income)}</p>
                </div>
                <div className="bg-rose-50 border border-rose-200 rounded-xl p-5">
                  <p className="text-sm text-rose-600">Total Expenses</p>
                  <p className="text-2xl font-bold text-rose-700">{formatCurrency(financialData.total_expenses)}</p>
                </div>
                <div className="bg-indigo-50 border border-indigo-200 rounded-xl p-5">
                  <p className="text-sm text-indigo-600">Net Income</p>
                  <p className={`text-2xl font-bold ${financialData.net_income >= 0 ? 'text-emerald-700' : 'text-rose-700'}`}>
                    {formatCurrency(financialData.net_income)}
                  </p>
                </div>
              </div>

              {/* Monthly Trend Chart */}
              {financialData.monthly_breakdown && financialData.monthly_breakdown.length > 0 && (
                <div className="bg-white rounded-xl border border-slate-200 p-5">
                  <h4 className="font-semibold text-slate-900 mb-4">Monthly Trend</h4>
                  <ResponsiveContainer width="100%" height={300}>
                    <LineChart data={financialData.monthly_breakdown}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                      <XAxis dataKey="month" tick={{ fontSize: 12 }} />
                      <YAxis tick={{ fontSize: 12 }} tickFormatter={(v) => `$${v/1000}k`} />
                      <Tooltip formatter={(value) => formatCurrency(value)} />
                      <Line type="monotone" dataKey="income" stroke="#10b981" strokeWidth={2} name="Income" />
                      <Line type="monotone" dataKey="expenses" stroke="#ef4444" strokeWidth={2} name="Expenses" />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              )}

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="bg-white rounded-xl border border-slate-200 p-5">
                  <h4 className="font-semibold text-slate-900 mb-4">Income by Category</h4>
                  {financialData.income_by_category.length > 0 ? (
                    <ResponsiveContainer width="100%" height={250}>
                      <PieChart>
                        <Pie data={financialData.income_by_category} cx="50%" cy="50%" outerRadius={80} dataKey="amount" nameKey="category"
                          label={({ category, percent }) => `${(percent * 100).toFixed(0)}%`}>
                          {financialData.income_by_category.map((entry, index) => <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />)}
                        </Pie>
                        <Tooltip formatter={(value) => formatCurrency(value)} />
                      </PieChart>
                    </ResponsiveContainer>
                  ) : <p className="text-slate-500 text-center py-8">No income data</p>}
                </div>

                <div className="bg-white rounded-xl border border-slate-200 p-5">
                  <h4 className="font-semibold text-slate-900 mb-4">Expenses by Category</h4>
                  {financialData.expenses_by_category.length > 0 ? (
                    <ResponsiveContainer width="100%" height={250}>
                      <BarChart data={financialData.expenses_by_category} layout="vertical">
                        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                        <XAxis type="number" tick={{ fontSize: 12 }} tickFormatter={(v) => `$${v/1000}k`} />
                        <YAxis type="category" dataKey="category" tick={{ fontSize: 11 }} width={100} />
                        <Tooltip formatter={(value) => formatCurrency(value)} />
                        <Bar dataKey="amount" fill="#7c3aed" radius={[0, 4, 4, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  ) : <p className="text-slate-500 text-center py-8">No expense data</p>}
                </div>
              </div>
            </div>
          )}

          {activeReport === 'aged-ar' && renderAgedBuckets(agedAR, 'Aged Accounts Receivable')}
          {activeReport === 'aged-ap' && renderAgedBuckets(agedAP, 'Aged Accounts Payable')}

          {activeReport === 'board' && boardReport && (
            <div className="space-y-6">
              <div className="bg-gradient-to-r from-indigo-600 to-violet-600 rounded-xl p-6 text-white">
                <h3 className="text-xl font-bold">{boardReport.corporation.name}</h3>
                <p className="text-indigo-100">Board Report - {boardReport.report_date}</p>
                <p className="text-sm text-indigo-200 mt-1">{boardReport.corporation.property_type} Property</p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="bg-white rounded-xl border border-slate-200 p-5">
                  <div className="flex items-center gap-3"><Building className="w-5 h-5 text-indigo-600" /><p className="text-sm text-slate-500">Occupancy</p></div>
                  <p className="text-2xl font-bold text-slate-900 mt-2">{boardReport.units_summary.occupancy_rate}%</p>
                  <p className="text-xs text-slate-400">{boardReport.units_summary.occupied} of {boardReport.units_summary.total} units</p>
                </div>
                <div className="bg-white rounded-xl border border-slate-200 p-5">
                  <div className="flex items-center gap-3"><DollarSign className="w-5 h-5 text-emerald-600" /><p className="text-sm text-slate-500">YTD Net Income</p></div>
                  <p className={`text-2xl font-bold mt-2 ${boardReport.financial_summary.ytd_net_income >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>
                    {formatCurrency(boardReport.financial_summary.ytd_net_income)}
                  </p>
                </div>
                <div className="bg-white rounded-xl border border-slate-200 p-5">
                  <div className="flex items-center gap-3"><CreditCard className="w-5 h-5 text-cyan-600" /><p className="text-sm text-slate-500">Cash on Hand</p></div>
                  <p className="text-2xl font-bold text-slate-900 mt-2">{formatCurrency(boardReport.financial_summary.total_cash_on_hand)}</p>
                </div>
                <div className="bg-white rounded-xl border border-slate-200 p-5">
                  <div className="flex items-center gap-3"><PiggyBank className="w-5 h-5 text-violet-600" /><p className="text-sm text-slate-500">Reserve Fund</p></div>
                  <p className="text-2xl font-bold text-indigo-600 mt-2">{formatCurrency(boardReport.financial_summary.reserve_fund_balance)}</p>
                </div>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="bg-white rounded-xl border border-slate-200 p-5">
                  <h4 className="font-semibold text-slate-900 mb-4">Financial Summary (YTD)</h4>
                  <div className="space-y-3">
                    <div className="flex justify-between"><span className="text-slate-600">Total Income</span><span className="font-semibold text-emerald-600">{formatCurrency(boardReport.financial_summary.ytd_income)}</span></div>
                    <div className="flex justify-between"><span className="text-slate-600">Total Expenses</span><span className="font-semibold text-rose-600">{formatCurrency(boardReport.financial_summary.ytd_expenses)}</span></div>
                    <div className="flex justify-between pt-3 border-t"><span className="font-semibold">Net Income</span><span className={`font-bold ${boardReport.financial_summary.ytd_net_income >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>{formatCurrency(boardReport.financial_summary.ytd_net_income)}</span></div>
                  </div>
                </div>

                <div className="bg-white rounded-xl border border-slate-200 p-5">
                  <h4 className="font-semibold text-slate-900 mb-4">Maintenance Summary</h4>
                  <div className="space-y-3">
                    <div className="flex justify-between"><span className="text-slate-600">Open Requests</span><span className="font-semibold text-amber-600">{boardReport.maintenance_summary.open_requests}</span></div>
                    <div className="flex justify-between"><span className="text-slate-600">Completed</span><span className="font-semibold text-emerald-600">{boardReport.maintenance_summary.completed_requests}</span></div>
                    <div className="flex justify-between pt-3 border-t"><span className="font-semibold">Completion Rate</span><span className="font-bold text-indigo-600">{boardReport.maintenance_summary.completion_rate}%</span></div>
                  </div>
                </div>
              </div>

              {/* Collection Summary */}
              {boardReport.collection_summary && Object.keys(boardReport.collection_summary).length > 0 && (
                <div className="bg-white rounded-xl border border-slate-200 p-5">
                  <h4 className="font-semibold text-slate-900 mb-4">Collections Summary (YTD)</h4>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {Object.entries(boardReport.collection_summary).map(([type, data]) => (
                      <div key={type} className="bg-slate-50 rounded-lg p-3">
                        <p className="text-xs text-slate-500 capitalize">{type.replace('_', ' ')}</p>
                        <p className="text-lg font-bold text-slate-900">{formatCurrency(data.total)}</p>
                        <p className="text-xs text-slate-400">{data.count} payments</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Expiring Leases */}
              {boardReport.expiring_leases && boardReport.expiring_leases.length > 0 && (
                <div className="bg-white rounded-xl border border-slate-200 p-5">
                  <h4 className="font-semibold text-slate-900 mb-4">Leases Expiring in 90 Days</h4>
                  <table className="w-full">
                    <thead><tr className="border-b"><th className="py-2 text-left text-xs text-slate-500">Unit</th><th className="py-2 text-left text-xs text-slate-500">Tenant</th><th className="py-2 text-left text-xs text-slate-500">End Date</th><th className="py-2 text-right text-xs text-slate-500">Monthly</th></tr></thead>
                    <tbody>
                      {boardReport.expiring_leases.map((lease, i) => (
                        <tr key={i} className="border-b last:border-0"><td className="py-2 text-sm">{lease.unit_number}</td><td className="py-2 text-sm text-slate-600">{lease.tenant_name}</td><td className="py-2 text-sm text-amber-600">{lease.end_date}</td><td className="py-2 text-sm text-right font-medium">{formatCurrency(lease.total_monthly)}</td></tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Bank Accounts */}
              <div className="bg-white rounded-xl border border-slate-200 p-5">
                <h4 className="font-semibold text-slate-900 mb-4">Bank Accounts</h4>
                <table className="w-full">
                  <thead><tr className="border-b"><th className="py-2 text-left text-xs text-slate-500">Account</th><th className="py-2 text-left text-xs text-slate-500">Bank</th><th className="py-2 text-left text-xs text-slate-500">Type</th><th className="py-2 text-right text-xs text-slate-500">Balance</th></tr></thead>
                  <tbody>
                    {boardReport.bank_accounts.map((account) => (
                      <tr key={account.id} className="border-b last:border-0"><td className="py-3 font-medium">{account.account_name}</td><td className="py-3 text-slate-600">{account.bank_name}</td><td className="py-3"><span className="text-xs px-2 py-1 rounded-full bg-slate-100 capitalize">{account.account_type}</span></td><td className="py-3 text-right font-semibold">{formatCurrency(account.current_balance)}</td></tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {activeReport === 'board' && !selectedProperty && (
            <div className="text-center py-12 bg-white rounded-xl border border-slate-200">
              <Building className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-500">Please select a property to view the Board Report</p>
            </div>
          )}

          {/* Reconciliation Report */}
          {activeReport === 'reconciliation' && reconciliationReport && (
            <div className="space-y-6">
              {/* Summary Header */}
              <div className="bg-gradient-to-r from-indigo-600 to-violet-600 rounded-xl p-6 text-white">
                <div className="flex justify-between items-start">
                  <div>
                    <h3 className="text-lg font-semibold">Reconciliation Report</h3>
                    <p className="text-indigo-100 text-sm">As of {reconciliationReport.report_date}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-3xl font-bold">{reconciliationReport.summary?.reconciliation_rate || 0}%</p>
                    <p className="text-sm text-indigo-100">Reconciled</p>
                  </div>
                </div>
              </div>

              {/* Summary Cards */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div className="bg-white rounded-xl border border-slate-200 p-5">
                  <div className="flex items-center gap-3">
                    <div className="bg-blue-100 p-2 rounded-lg"><Scale className="w-5 h-5 text-blue-600" /></div>
                    <div>
                      <p className="text-2xl font-bold text-blue-600">{reconciliationReport.summary?.total_bank_transactions || 0}</p>
                      <p className="text-sm text-slate-500">Total Bank Txns</p>
                    </div>
                  </div>
                </div>
                <div className="bg-white rounded-xl border border-slate-200 p-5">
                  <div className="flex items-center gap-3">
                    <div className="bg-emerald-100 p-2 rounded-lg"><CheckCircle2 className="w-5 h-5 text-emerald-600" /></div>
                    <div>
                      <p className="text-2xl font-bold text-emerald-600">{reconciliationReport.summary?.matched || 0}</p>
                      <p className="text-sm text-slate-500">Matched</p>
                    </div>
                  </div>
                </div>
                <div className="bg-white rounded-xl border border-slate-200 p-5">
                  <div className="flex items-center gap-3">
                    <div className="bg-amber-100 p-2 rounded-lg"><AlertCircle className="w-5 h-5 text-amber-600" /></div>
                    <div>
                      <p className="text-2xl font-bold text-amber-600">{reconciliationReport.summary?.unmatched || 0}</p>
                      <p className="text-sm text-slate-500">Unmatched</p>
                    </div>
                  </div>
                </div>
                <div className="bg-white rounded-xl border border-slate-200 p-5">
                  <div className="flex items-center gap-3">
                    <div className="bg-violet-100 p-2 rounded-lg"><FileText className="w-5 h-5 text-violet-600" /></div>
                    <div>
                      <p className="text-2xl font-bold text-violet-600">{reconciliationReport.adjustments?.total_count || 0}</p>
                      <p className="text-sm text-slate-500">Adjustments</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Matched vs Unmatched Totals */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="bg-white rounded-xl border border-slate-200 p-5">
                  <h4 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
                    <CheckCircle2 className="w-5 h-5 text-emerald-600" /> Matched Totals
                  </h4>
                  <div className="space-y-3">
                    <div className="flex justify-between"><span className="text-slate-600">Income</span><span className="font-semibold text-emerald-600">{formatCurrency(reconciliationReport.matched_totals?.income)}</span></div>
                    <div className="flex justify-between"><span className="text-slate-600">Expenses</span><span className="font-semibold text-rose-600">{formatCurrency(reconciliationReport.matched_totals?.expense)}</span></div>
                    <div className="flex justify-between pt-3 border-t"><span className="font-semibold">Net</span><span className={`font-bold ${(reconciliationReport.matched_totals?.net || 0) >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>{formatCurrency(reconciliationReport.matched_totals?.net)}</span></div>
                  </div>
                </div>

                <div className="bg-white rounded-xl border border-slate-200 p-5">
                  <h4 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
                    <AlertCircle className="w-5 h-5 text-amber-600" /> Unmatched Totals
                  </h4>
                  <div className="space-y-3">
                    <div className="flex justify-between"><span className="text-slate-600">Income</span><span className="font-semibold text-emerald-600">{formatCurrency(reconciliationReport.unmatched_totals?.income)}</span></div>
                    <div className="flex justify-between"><span className="text-slate-600">Expenses</span><span className="font-semibold text-rose-600">{formatCurrency(reconciliationReport.unmatched_totals?.expense)}</span></div>
                    <div className="flex justify-between pt-3 border-t"><span className="font-semibold">Net</span><span className={`font-bold ${(reconciliationReport.unmatched_totals?.net || 0) >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>{formatCurrency(reconciliationReport.unmatched_totals?.net)}</span></div>
                  </div>
                </div>
              </div>

              {/* Adjustments by Category */}
              {reconciliationReport.adjustments?.by_category && reconciliationReport.adjustments.by_category.length > 0 && (
                <div className="bg-white rounded-xl border border-slate-200 p-5">
                  <h4 className="font-semibold text-slate-900 mb-4">Adjustments by Category</h4>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {reconciliationReport.adjustments.by_category.map((cat) => (
                      <div key={cat.category} className="bg-slate-50 rounded-lg p-3">
                        <p className="text-xs text-slate-500 capitalize">{cat.category.replace(/_/g, ' ')}</p>
                        <p className={`text-lg font-bold ${cat.total >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>{formatCurrency(cat.total)}</p>
                        <p className="text-xs text-slate-400">{cat.count} items</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Unmatched Items */}
              {reconciliationReport.unmatched_items && reconciliationReport.unmatched_items.length > 0 && (
                <div className="bg-white rounded-xl border border-slate-200 p-5">
                  <h4 className="font-semibold text-slate-900 mb-4">Unmatched Bank Transactions</h4>
                  <table className="w-full">
                    <thead><tr className="border-b"><th className="py-2 text-left text-xs text-slate-500">Date</th><th className="py-2 text-left text-xs text-slate-500">Description</th><th className="py-2 text-left text-xs text-slate-500">Type</th><th className="py-2 text-right text-xs text-slate-500">Amount</th></tr></thead>
                    <tbody>
                      {reconciliationReport.unmatched_items.map((item) => (
                        <tr key={item.id} className="border-b last:border-0">
                          <td className="py-2 text-sm">{item.date}</td>
                          <td className="py-2 text-sm text-slate-600">{item.description}</td>
                          <td className="py-2"><span className={`text-xs px-2 py-1 rounded-full ${item.type === 'income' ? 'bg-emerald-100 text-emerald-700' : 'bg-rose-100 text-rose-700'}`}>{item.type}</span></td>
                          <td className={`py-2 text-sm text-right font-medium ${item.amount >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>{formatCurrency(item.amount)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}
